import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { SheetActions, SheetBody } from '@/components/custom/sheet-layout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { DeleteButton } from '@/components/custom/delete-button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import type { AddTeamMemberRequest } from '@vagusx/clients';
import { useCreateTeam, useDeleteTeam, useTeam, useTeams, useUpdateTeam } from '@/hooks/use-teams';
import type { DraftMember } from './draft-member';
import { toDraft } from './draft-member';
import { inboundDefaultChange, isInboundCallTeam } from './inbound-default';
import { MemberList } from './MemberList';
import { MemberPicker, MemberSectionHeader } from './MemberPicker';

interface FormValues {
  name: string;
  description: string;
}

interface TeamEditorProps {
  teamId?: string;
  onClose: () => void;
  /**
   * Fired after a brand-new team is created. Parent transitions the
   * drawer into view mode for the new team so the user can confirm
   * what was saved.
   */
  onCreated?: (teamId: string) => void;
}

export function TeamEditor({ teamId, onClose, onCreated }: TeamEditorProps): React.ReactElement {
  const isEdit = !!teamId;
  const { data: team } = useTeam(teamId);
  // The list already carries every team's flag and channel: enough to name
  // the team a new default would displace, without another request.
  const { data: allTeams } = useTeams();

  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const deleteTeam = useDeleteTeam();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { name: '', description: '' },
  });

  // Single source of truth for the in-flight member list. Hydrated from
  // the server when the team loads (edit mode); starts empty in create.
  const [draftMembers, setDraftMembers] = useState<DraftMember[]>([]);
  const [inboundDefault, setInboundDefault] = useState(false);
  const wasInboundDefault = !!team && isInboundCallTeam(team);
  const currentInboundTeam = allTeams?.find((t) => isInboundCallTeam(t) && t.id !== teamId);

  useEffect(() => {
    if (team) {
      reset({ name: team.name, description: team.description ?? '' });
      setDraftMembers(team.members.map(toDraft));
      setInboundDefault(isInboundCallTeam(team));
    }
  }, [team, reset]);

  const onSubmit = async (data: FormValues): Promise<void> => {
    const members: AddTeamMemberRequest[] = draftMembers.map((m) => ({
      memberType: m.memberType,
      userId: m.userId,
      agentId: m.agentId,
    }));

    if (isEdit && teamId) {
      await updateTeam.mutateAsync({
        id: teamId,
        data: {
          name: data.name,
          description: data.description || undefined,
          members,
          ...inboundDefaultChange(wasInboundDefault, inboundDefault),
        },
      });
      toast.success('Team saved');
    } else {
      const created = await createTeam.mutateAsync({
        name: data.name,
        description: data.description || undefined,
        // Channel is required at the DB layer. A team that takes inbound
        // calls is a phone team; otherwise it is only an assignment target,
        // and email has always been the default.
        channel: inboundDefault ? 'phone' : 'email',
        ...(inboundDefault ? { isInboundDefault: true } : {}),
        members,
      });
      toast.success('Team created');
      if (onCreated) onCreated(created.id);
      else onClose();
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!teamId) return;
    await deleteTeam.mutateAsync(teamId);
    toast.success('Team deleted');
    onClose();
  };

  const addDraft = (m: DraftMember): void =>
    setDraftMembers((prev) =>
      // Guard against double-add on rapid clicks — keyed by userId/agentId.
      prev.some(
        (existing) =>
          (m.userId && existing.userId === m.userId) ||
          (m.agentId && existing.agentId === m.agentId)
      )
        ? prev
        : [...prev, m]
    );

  const removeDraft = (draftId: string): void =>
    setDraftMembers((prev) => prev.filter((m) => m.draftId !== draftId));

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
      <SheetHeader>
        <SheetTitle>{isEdit ? 'Edit team' : 'New team'}</SheetTitle>
        <SheetDescription>
          {isEdit
            ? 'Update team settings and members. Changes apply on Save.'
            : 'Create a team and add members. Everything is saved together.'}
        </SheetDescription>
      </SheetHeader>

      <SheetBody className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="team-name">Name</Label>
          <Input
            id="team-name"
            {...register('name', { required: 'Name is required', maxLength: 200 })}
            placeholder="e.g. Front Office"
          />
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="team-description">Description</Label>
          <Input
            id="team-description"
            {...register('description', { maxLength: 500 })}
            placeholder="What does this team handle?"
          />
        </div>

        <div className="flex items-start justify-between gap-4 rounded-md border p-3">
          <div className="space-y-1">
            <Label htmlFor="team-inbound-default">Default team for inbound calls</Label>
            <p className="text-xs text-muted-foreground">
              Tasks from inbound calls (callbacks, messages, missed bookings, failed calls) are
              assigned to this team when no assignment rule claims them.
            </p>
            {inboundDefault && currentInboundTeam && (
              <p className="text-xs text-muted-foreground">
                Saving moves this from {currentInboundTeam.name}.
              </p>
            )}
          </div>
          <Switch
            id="team-inbound-default"
            checked={inboundDefault}
            onCheckedChange={setInboundDefault}
          />
        </div>

        <Separator />

        <div className="space-y-2">
          <MemberSectionHeader count={draftMembers.length}>
            <MemberPicker
              channel={inboundDefault ? 'phone' : (team?.channel ?? 'email')}
              members={draftMembers}
              onAdd={addDraft}
            />
          </MemberSectionHeader>
          <MemberList members={draftMembers} onRemove={removeDraft} />
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Check className="h-3 w-3" />
            Tasks assigned to this team appear in every member&apos;s &quot;My Tasks&quot; view.
          </p>
        </div>
      </SheetBody>

      <SheetActions
        start={isEdit && <DeleteButton onClick={handleDelete} isPending={deleteTeam.isPending} />}
      >
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <SaveButton type="submit" isPending={isSubmitting} isEdit={isEdit} />
      </SheetActions>
    </form>
  );
}
